import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";

/* ---------- KONŠTANTY MIMO KOMPONENTU ---------- */

const images = [
  "/20230801_141813.jpg",
  "/20230801_142219.jpg",
  "/20230801_142228.jpg",
  "/20230802_101425.jpg",
  "/20230802_101520.jpg",
  "/IMG_20230802_082338.jpg",
  "/IMG_20230803_114443.jpg",

] as const;

const imageVariants = {
  hover: { scale: 1.05, transition: { duration: 0.3 } },
  tap: { scale: 0.95 },
};

/* ----------------- KOMPONENT ------------------- */

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isHovered, setIsHovered] = useState(false);

  const carouselRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const scrollLeft = useRef(0);

  /* ===== Auto-scroll ===== */
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;

    let pos = 0;
    const speed = 0.5; // plynulejší posun

    let animationFrameId: number;

    const step = () => {
      if (isHovered || isDragging.current) {
        animationFrameId = requestAnimationFrame(step);
        return;
      }

      pos = el.scrollLeft + speed;
      const max = el.scrollWidth - el.clientWidth;

      if (pos >= max) {
        el.scrollLeft = 0;
      } else {
        el.scrollLeft = pos;
      }

      animationFrameId = requestAnimationFrame(step);
    };

    animationFrameId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isHovered]);


  /* ===== Slideshow (vybraná veľká fotka) ===== */
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      if (!isHovered && !isDragging.current) {
        setSelectedImage(prev => {
          if (prev !== images[i]) return images[i];
          return prev;
        });
        i = (i + 1) % images.length;
      }
    }, 5000);
    return () => clearInterval(id);
  }, [isHovered]);

  /* ===== Drag-to-scroll ===== */
  const handleDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = carouselRef.current;
    if (!el) return;
    isDragging.current = true;
    startX.current = e.pageX - el.offsetLeft;
    scrollLeft.current = el.scrollLeft;
  };

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = carouselRef.current;
    if (!isDragging.current || !el) return;
    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = (x - startX.current) * 2; // zrýchlenie posunu
    el.scrollLeft = scrollLeft.current - walk;
  };

  const stopDrag = () => (isDragging.current = false);

  /* ===== Tlačidlá ===== */
  const scrollBy = (delta: number) =>
    carouselRef.current?.scrollBy({ left: delta, behavior: "smooth" });

  /* ===== JSX ===== */
  return (
    <section
      id="gallery"
      className="min-h-fit bg-navy text-cream w-full overflow-hidden relative py-6 sm:py-8 md:py-12"
    >
      <motion.h2
        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-4 sm:mb-6 md:mb-8 uppercase tracking-tight drop-shadow-2xl"
        style={{ fontFamily: "Baloo 2", fontWeight: 800 }}
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
        viewport={{ once: true }}
      >
        Galéria
      </motion.h2>

      {/* --------- Vybraná fotka --------- */}
      <motion.div
        className="bg-white rounded-2xl shadow-xl border-2 border-primary p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 md:mb-8 max-w-full mx-auto aspect-[16/9] max-h-[60vh] flex items-center justify-center relative overflow-hidden"
        initial={{ opacity: 0, scale: 0.9 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        {selectedImage ? (
          <motion.img
            key={selectedImage}
            src={selectedImage}
            alt="Vybraná fotka"
            className="w-full h-full object-contain rounded-lg"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          />
        ) : (
          <p className="text-base sm:text-lg md:text-xl text-navy font-bold text-center">
            Klikni na fotku alebo počkaj na automatické prepínanie!
          </p>
        )}
      </motion.div>

      {/* --------- Karusel + navigačné šípky --------- */}
      <div
        className="relative max-w-full overflow-hidden px-4 sm:px-6"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Šípka vľavo */}
        <motion.button
          className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 bg-primary text-cream w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center z-10 shadow-md"
          whileHover={{ scale: 1.1, backgroundColor: "#F97316" }}
          whileTap={{ scale: 0.9 }}
          onClick={() => scrollBy(-200)}
        >
          <svg
            className="h-4 sm:h-5 w-4 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </motion.button>

        {/* Samotný karusel */}
        <motion.div
          ref={carouselRef}
          className="flex flex-nowrap space-x-[1vw] snap-x snap-mandatory w-full py-3 sm:py-4 cursor-grab select-none overflow-x-auto scrollbar-hide"
          onMouseDown={handleDown}
          onMouseMove={handleMove}
          onMouseUp={stopDrag}
          onMouseLeave={stopDrag}
        >
          {images.map((img, i) => (
            <motion.div
              key={img}
              className="flex-shrink-0 w-[25vw] sm:w-[10vw] md:w-[13vw] max-w-[310px] aspect-square snap-center relative rounded-lg overflow-hidden shadow-md border-2 border-secondary cursor-pointer"
              variants={imageVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={() => setSelectedImage(img)}
            >
              <img
                src={img}
                alt={`Galéria ${i + 1}`}
                className="w-full h-full object-cover rounded-lg"
                loading="lazy"
              />
              <motion.div
                className="absolute inset-0 bg-primary/50 flex items-center justify-center text-cream text-xs sm:text-sm md:text-base font-bold uppercase opacity-0"
                whileHover={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                Zobraziť
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Šípka vpravo */}
        <motion.button
          className="absolute right-4 sm:right-6 top-1/2 -translate-y-1/2 bg-primary text-cream w-8 sm:w-10 h-8 sm:h-10 rounded-full flex items-center justify-center z-10 shadow-md"
          whileHover={{ scale: 1.1, backgroundColor: "#F97316" }}
          whileTap={{ scale: 0.9 }}
          onClick={() => scrollBy(200)}
        >
          <svg
            className="h-4 sm:h-5 w-4 sm:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </motion.button>
      </div>
    </section>
  );
};

export default Gallery;
